import { access, readFile, readdir } from "node:fs/promises";
import path from "node:path";
import type { JsonObject, ToolDef } from "@fabric-harness/sdk";
import {
  type DatabricksCommandResult,
  type DatabricksCommandRunner,
  runDatabricksCommand,
} from "./app-deployment.js";
import {
  DatabricksManagedResourceConflictError,
  type DatabricksManagedResourceStore,
  MemoryDatabricksManagedResourceStore,
  databricksResourceFingerprint,
  requireDatabricksManagedResource,
} from "./managed-resource.js";

const BUNDLE_RESOURCE_TYPE = "databricks-bundle";
/** Directories excluded from bundle source fingerprints: CLI state and dependency/build output. */
const FINGERPRINT_EXCLUDED_DIRS = new Set([".databricks", "node_modules", "dist"]);
/** Bundle names reach the model as tool input and the store as resource ids, so keep them plain. */
const BUNDLE_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;

export interface DatabricksAssetBundleOptions {
  /** Directory containing the checked-in bundle's `databricks.yml`. */
  bundleDir: string;
  /**
   * Logical bundle name from the named `assetBundles` map; scopes the managed-resource identity so
   * lifecycles sharing one durable store never overwrite each other's records.
   */
  instanceName?: string;
  /** Bundle target (dev/prod) passed through to the Databricks CLI. */
  target?: string;
  /** Databricks CLI profile. */
  profile?: string;
  /** Databricks CLI executable; defaults to `databricks`. */
  executable?: string;
  env?: NodeJS.ProcessEnv;
  /** Injectable command boundary for tests and managed runners. */
  run?: DatabricksCommandRunner;
  /** Fingerprint manifest; defaults to an in-memory store. */
  store?: DatabricksManagedResourceStore;
  /** Label recorded as `createdBy`; never a token. */
  principalLabel?: string;
}

/** Structured handoff from bounded bundle submission to Jobs/Lakeflow polling tools. */
export interface DatabricksAssetBundleRunResult {
  submitted: true;
  resourceKey: string;
  runKind?: "job" | "pipeline";
  /** Jobs run id, directly accepted by the Databricks jobs status/output tools. */
  runId?: number;
  /** Lakeflow pipeline id, paired with `updateId` for pipeline status polling. */
  pipelineId?: string;
  /** Lakeflow update id, paired with `pipelineId` for pipeline status polling. */
  updateId?: string;
  runUrl?: string;
  output: string;
}

/**
 * Governed lifecycle for a checked-in Databricks Asset Bundle (DAB).
 *
 * Harness does not generate bundle YAML — it makes validate/deploy/run/destroy of an
 * existing bundle a fingerprinted, approval-gated operation:
 *
 * - `fingerprint()` hashes the bundle source tree (sorted relative paths + content),
 *   excluding `.databricks/` CLI state, `node_modules/`, and `dist/`.
 * - `deploy()` validates, deploys, then upserts the managed-resource record. When the
 *   caller passes `expectedFingerprint` and a record exists with a different fingerprint,
 *   deploy fails with `DatabricksManagedResourceConflictError` (optimistic concurrency so
 *   one agent cannot stomp a bundle recorded from another tree).
 * - `run()` submits a run of a bundle-defined job/pipeline by its resource key without
 *   waiting (`--no-wait`), so bundles deployed outside Harness (e.g. by CI) stay runnable;
 *   the approval gate is the governance boundary, not the managed-resource record.
 * - `destroy()` requires the managed record (`DatabricksUnmanagedResourceError` otherwise),
 *   refuses to destroy when the current tree fingerprint differs from the recorded one
 *   unless `force` is set, and deletes the record after a successful teardown.
 */
export class DatabricksAssetBundleLifecycle {
  /** Resolved bundle directory; also the governed resource identity on the tools. */
  readonly bundleDir: string;
  /** Logical name of this bundle within a named `assetBundles` map; unset in the single-bundle form. */
  readonly instanceName?: string;
  private readonly target?: string;
  private readonly profile?: string;
  private readonly executable: string;
  private readonly env: NodeJS.ProcessEnv;
  private readonly commandRunner: DatabricksCommandRunner;
  private readonly store: DatabricksManagedResourceStore;
  private readonly principalLabel: string;
  private bundleName: string | undefined;

  constructor(options: DatabricksAssetBundleOptions) {
    if (!options.bundleDir?.trim())
      throw new Error("databricks asset bundles: bundleDir is required.");
    this.bundleDir = path.resolve(options.bundleDir);
    this.executable = options.executable ?? "databricks";
    this.env = options.env ?? process.env;
    this.commandRunner = options.run ?? runDatabricksCommand;
    this.store = options.store ?? new MemoryDatabricksManagedResourceStore();
    this.principalLabel = options.principalLabel ?? "unknown";
    if (options.target !== undefined) this.target = options.target;
    if (options.profile !== undefined) this.profile = options.profile;
    if (options.instanceName !== undefined) this.instanceName = options.instanceName;
  }

  /** Run `databricks bundle validate` without mutating the workspace. */
  async validate(): Promise<{ valid: true; output: string }> {
    await this.requireManifest();
    const args = ["bundle", "validate", ...this.targetArgs(), ...this.profileArgs()] as const;
    const result = await this.commandRunner(this.executable, args, {
      cwd: this.bundleDir,
      env: this.env,
    });
    requireSuccess("Databricks bundle validation", result);
    return { valid: true, output: lastNonEmptyLine(result.stdout) };
  }

  /** Deterministic sha256 over the sorted bundle source tree (path + content). */
  async fingerprint(): Promise<string> {
    await this.requireManifest();
    const files = (await collectSourceFiles(this.bundleDir)).sort();
    const entries = [];
    for (const file of files) {
      const content = await readFile(file);
      const digest = await crypto.subtle.digest("SHA-256", content);
      entries.push({
        path: path.relative(this.bundleDir, file).split(path.sep).join("/"),
        sha256: [...new Uint8Array(digest)]
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join(""),
      });
    }
    return databricksResourceFingerprint(entries);
  }

  /** Validate, deploy, and record the source fingerprint in the managed-resource store. */
  async deploy(
    options: { expectedFingerprint?: string } = {},
  ): Promise<{ deployed: true; fingerprint: string; output: string }> {
    await this.requireManifest();
    const fingerprint = await this.fingerprint();
    const resourceId = await this.resourceId();
    const existing = await this.store.get(BUNDLE_RESOURCE_TYPE, resourceId);
    if (
      existing &&
      options.expectedFingerprint !== undefined &&
      existing.fingerprint !== options.expectedFingerprint
    ) {
      throw new DatabricksManagedResourceConflictError({
        resourceType: BUNDLE_RESOURCE_TYPE,
        resourceId,
        expectedFingerprint: options.expectedFingerprint,
        actualFingerprint: existing.fingerprint,
      });
    }
    await this.validate();
    const args = ["bundle", "deploy", ...this.targetArgs(), ...this.profileArgs()] as const;
    const result = await this.commandRunner(this.executable, args, {
      cwd: this.bundleDir,
      env: this.env,
    });
    requireSuccess("Databricks bundle deployment", result);
    const now = new Date().toISOString();
    await this.store.put({
      resourceType: BUNDLE_RESOURCE_TYPE,
      resourceId,
      fingerprint,
      createdBy: existing?.createdBy ?? this.principalLabel,
      toolCallId: "databricks_bundle_deploy",
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    });
    return { deployed: true, fingerprint, output: lastNonEmptyLine(result.stdout) };
  }

  /**
   * Submit a run of a bundle-defined job/pipeline by its `databricks.yml` resource key.
   * Always passes `--no-wait` — waiting inside a tool call would violate finite-agent
   * boundedness, so run status must be polled through the bounded jobs/lakeflow status
   * tools. No managed-resource record is required: bundles deployed outside Harness
   * (e.g. by CI) stay runnable, and the CLI fails cleanly against an undeployed bundle.
   */
  async run(options: { key: string; restart?: boolean }): Promise<DatabricksAssetBundleRunResult> {
    await this.requireManifest();
    const key = options.key?.trim();
    if (!key) throw new Error("databricks asset bundles: run requires a non-empty resource key.");
    const args = [
      "bundle",
      "run",
      key,
      "--no-wait",
      ...(options.restart === true ? ["--restart"] : []),
      "--output",
      "json",
      "--progress-format",
      "json",
      ...this.targetArgs(),
      ...this.profileArgs(),
    ] as const;
    const result = await this.commandRunner(this.executable, args, {
      cwd: this.bundleDir,
      env: this.env,
    });
    requireSuccess("Databricks bundle run submission", result);
    return parseBundleRunResult(key, result);
  }

  /** Destroy a Harness-recorded bundle deployment and delete its managed record. */
  async destroy(options: { force?: boolean } = {}): Promise<{ destroyed: true; output: string }> {
    await this.requireManifest();
    const resourceId = await this.resourceId();
    const record = await requireDatabricksManagedResource({
      store: this.store,
      resourceType: BUNDLE_RESOURCE_TYPE,
      resourceId,
    });
    if (!options.force) {
      const current = await this.fingerprint();
      if (current !== record.fingerprint)
        throw new DatabricksManagedResourceConflictError({
          resourceType: BUNDLE_RESOURCE_TYPE,
          resourceId,
          expectedFingerprint: record.fingerprint,
          actualFingerprint: current,
        });
    }
    const args = [
      "bundle",
      "destroy",
      "--auto-approve",
      ...this.targetArgs(),
      ...this.profileArgs(),
    ] as const;
    const result = await this.commandRunner(this.executable, args, {
      cwd: this.bundleDir,
      env: this.env,
    });
    requireSuccess("Databricks bundle teardown", result);
    await this.store.delete(BUNDLE_RESOURCE_TYPE, resourceId);
    return { destroyed: true, output: lastNonEmptyLine(result.stdout) };
  }

  /**
   * Bundle `name:` parsed from `databricks.yml`, falling back to the directory basename. Named
   * bundles append `#instanceName` (and `#target` when set) so two lifecycles over the same manifest
   * cannot collide in a shared managed-resource store; the single-bundle form keeps the bare name so
   * existing records stay addressable.
   */
  private async resourceId(): Promise<string> {
    if (this.bundleName === undefined) {
      const manifest = await readFile(path.join(this.bundleDir, "databricks.yml"), "utf8");
      const match = /^\s*name:\s*["']?([^\s"']+)["']?\s*$/m.exec(manifest);
      this.bundleName = match?.[1] ?? path.basename(this.bundleDir);
    }
    if (this.instanceName === undefined) return this.bundleName;
    return `${this.bundleName}#${this.instanceName}${this.target ? `#${this.target}` : ""}`;
  }

  private async requireManifest(): Promise<void> {
    const manifest = path.join(this.bundleDir, "databricks.yml");
    try {
      await access(manifest);
    } catch {
      throw new Error(`databricks asset bundles: databricks.yml not found at ${manifest}.`);
    }
  }

  private targetArgs(): string[] {
    return this.target ? ["--target", this.target] : [];
  }

  private profileArgs(): string[] {
    return this.profile ? ["--profile", this.profile] : [];
  }
}

export function databricksAssetBundleLifecycle(
  options: DatabricksAssetBundleOptions,
): DatabricksAssetBundleLifecycle {
  return new DatabricksAssetBundleLifecycle(options);
}

/** Several checked-in bundles keyed by logical name; the key becomes the lifecycle's `instanceName`. */
export type DatabricksAssetBundleMapOptions = Record<
  string,
  Omit<DatabricksAssetBundleOptions, "instanceName">
>;

/**
 * Discriminates the single-bundle form from the named-bundle map. A map key literally called
 * `bundleDir` still selects map mode because its value is an entry object, not a string.
 */
export function isDatabricksAssetBundleEntry(
  value: unknown,
): value is { bundleDir: string } | DatabricksAssetBundleLifecycle {
  if (value instanceof DatabricksAssetBundleLifecycle) return true;
  return (
    isPlainObject(value) &&
    Object.hasOwn(value, "bundleDir") &&
    typeof (value as { bundleDir?: unknown }).bundleDir === "string"
  );
}

/**
 * Reject a named-bundle container that is not a plain object. Arrays are rejected too (isPlainObject
 * excludes them): from untyped or parsed config they would otherwise enumerate as bundles named
 * "0", "1", … Shared with `databricks()` so the config boundary and this helper report one message.
 * Only the container is checked, so the assertion says nothing about the entries — each one is
 * validated during enumeration in `databricksAssetBundleLifecycles`.
 */
export function requireDatabricksAssetBundleMap(
  value: unknown,
): asserts value is Record<string, unknown> {
  if (!isPlainObject(value))
    throw new Error(
      "databricks asset bundles: named bundles must be an object keyed by bundle name.",
    );
}

/**
 * Build one lifecycle per named bundle. Names are validated (they reach the model as tool input and
 * the managed-resource store as ids) and sorted, so tool enums and dispatch stay deterministic.
 * `defaults` are applied under each entry, letting a caller set one `principalLabel` for every bundle.
 */
export function databricksAssetBundleLifecycles(
  options: DatabricksAssetBundleMapOptions,
  defaults: Omit<DatabricksAssetBundleOptions, "bundleDir" | "instanceName"> = {},
): Map<string, DatabricksAssetBundleLifecycle> {
  requireDatabricksAssetBundleMap(options);
  const lifecycles = new Map<string, DatabricksAssetBundleLifecycle>();
  for (const name of Object.keys(options).sort()) {
    requireBundleName(name);
    const entry = options[name];
    if (!isPlainObject(entry) || typeof entry.bundleDir !== "string" || !entry.bundleDir.trim())
      throw new Error(
        `databricks asset bundles: bundle "${name}" requires a non-empty bundleDir string.`,
      );
    lifecycles.set(
      name,
      new DatabricksAssetBundleLifecycle({ ...defaults, ...entry, instanceName: name }),
    );
  }
  requireNamedBundles(lifecycles);
  return lifecycles;
}

/**
 * Model-facing tools for the governed bundle lifecycle. Write tools are approval-gated
 * by the `databricks()` bundle like every other authoring surface.
 *
 * A single bundle keeps bundle-free tool schemas; a named map adds a required `bundle` selector to
 * every schema (even with one entry, so the schema and its approval digests stay stable when a second
 * bundle is added) and governs only the bundle the call actually names.
 */
export function databricksAssetBundleTools(
  options:
    | DatabricksAssetBundleOptions
    | DatabricksAssetBundleLifecycle
    | DatabricksAssetBundleMapOptions
    | Map<string, DatabricksAssetBundleLifecycle>,
): ToolDef[] {
  if (isDatabricksAssetBundleEntry(options)) return singleBundleTools(options);
  return namedBundleTools(
    options instanceof Map ? checkedLifecycles(options) : databricksAssetBundleLifecycles(options),
  );
}

/**
 * Validate and snapshot a caller-supplied lifecycle map. The copy matters: the enum, the governed
 * lineage, and the approval digest are all fixed when the tools are built, so a later
 * `map.set("jobs", other)` must not silently redirect an already-approved `{ bundle: "jobs" }` call.
 * Each lifecycle must also carry `instanceName === name`: without it the record ids stay the legacy
 * bare manifest name (two bundles sharing a manifest would then overwrite each other in a durable
 * store), and a mismatch would split the lineage identity from the stored one.
 */
function checkedLifecycles(
  lifecycles: ReadonlyMap<string, DatabricksAssetBundleLifecycle>,
): Map<string, DatabricksAssetBundleLifecycle> {
  const checked = new Map<string, DatabricksAssetBundleLifecycle>();
  for (const [name, lifecycle] of lifecycles) {
    requireBundleName(name);
    if (!(lifecycle instanceof DatabricksAssetBundleLifecycle))
      throw new Error(
        `databricks asset bundles: bundle "${name}" must be a DatabricksAssetBundleLifecycle.`,
      );
    if (lifecycle.instanceName !== name)
      throw new Error(
        `databricks asset bundles: bundle "${name}" must be built with instanceName "${name}"${
          lifecycle.instanceName === undefined ? "" : `, not "${lifecycle.instanceName}"`
        }.`,
      );
    checked.set(name, lifecycle);
  }
  return checked;
}

/** Runtime map keys can be numbers or symbols, which would coerce past the pattern. */
function requireBundleName(name: unknown): asserts name is string {
  if (typeof name !== "string")
    throw new Error(`databricks asset bundles: bundle names must be strings, not ${typeof name}.`);
  if (!BUNDLE_NAME_PATTERN.test(name))
    throw new Error(
      `databricks asset bundles: bundle name "${name}" must match ${BUNDLE_NAME_PATTERN.source}.`,
    );
}

function singleBundleTools(
  options: DatabricksAssetBundleOptions | DatabricksAssetBundleLifecycle,
): ToolDef[] {
  const lifecycle =
    options instanceof DatabricksAssetBundleLifecycle
      ? options
      : new DatabricksAssetBundleLifecycle(options);
  const resources: JsonObject[] = [{ kind: "bundle", value: lifecycle.bundleDir }];
  return [
    bundleTool(
      "databricks_bundle_validate",
      "Validate the checked-in Databricks Asset Bundle without deploying it.",
      "read",
      { type: "object", additionalProperties: false, properties: {} },
      [],
      () => lifecycle.validate(),
    ),
    bundleTool(
      "databricks_bundle_deploy",
      "Validate and deploy the checked-in Databricks Asset Bundle, recording its source fingerprint.",
      "write",
      {
        type: "object",
        additionalProperties: false,
        properties: { expectedFingerprint: { type: "string" } },
      },
      resources,
      (input) => {
        const expectedFingerprint = (input as { expectedFingerprint?: string }).expectedFingerprint;
        return lifecycle.deploy(expectedFingerprint !== undefined ? { expectedFingerprint } : {});
      },
    ),
    bundleTool(
      "databricks_bundle_run",
      "Submit (without waiting) a run of a bundle-defined job or pipeline by its databricks.yml resource key.",
      "execute",
      {
        type: "object",
        additionalProperties: false,
        properties: { key: { type: "string" }, restart: { type: "boolean" } },
        required: ["key"],
      },
      resources,
      (input) =>
        lifecycle.run({
          key: (input as { key: string }).key,
          restart: (input as { restart?: boolean }).restart === true,
        }),
    ),
    bundleTool(
      "databricks_bundle_destroy",
      "Destroy a Harness-recorded Databricks Asset Bundle deployment.",
      "write",
      {
        type: "object",
        additionalProperties: false,
        properties: { force: { type: "boolean" } },
      },
      resources,
      (input) => lifecycle.destroy({ force: (input as { force?: boolean }).force === true }),
    ),
  ];
}

function namedBundleTools(
  lifecycles: ReadonlyMap<string, DatabricksAssetBundleLifecycle>,
): ToolDef[] {
  requireNamedBundles(lifecycles);
  const names = [...lifecycles.keys()].sort();
  const selector: JsonObject = { bundle: { type: "string", enum: names } };
  // A `/bundle` path descriptor, not a static list of every configured directory: lineage and the
  // approval digest then name only the bundle the call actually selected.
  const resources: JsonObject[] = [{ kind: "bundle", path: "/bundle" }];
  const select = (input: JsonObject): DatabricksAssetBundleLifecycle =>
    selectBundle(lifecycles, names, input);
  // Every executor is async so a rejected selection surfaces like any other lifecycle failure
  // instead of throwing synchronously out of the tool call.
  return [
    bundleTool(
      "databricks_bundle_validate",
      "Validate one checked-in Databricks Asset Bundle, selected by name with `bundle`, without deploying it.",
      "read",
      {
        type: "object",
        additionalProperties: false,
        properties: { ...selector },
        required: ["bundle"],
      },
      [],
      async (input) => select(input).validate(),
    ),
    bundleTool(
      "databricks_bundle_deploy",
      "Validate and deploy one checked-in Databricks Asset Bundle, selected by name with `bundle`, recording its source fingerprint.",
      "write",
      {
        type: "object",
        additionalProperties: false,
        properties: { ...selector, expectedFingerprint: { type: "string" } },
        required: ["bundle"],
      },
      resources,
      async (input) => {
        const lifecycle = select(input);
        const expectedFingerprint = (input as { expectedFingerprint?: string }).expectedFingerprint;
        return lifecycle.deploy(expectedFingerprint !== undefined ? { expectedFingerprint } : {});
      },
    ),
    bundleTool(
      "databricks_bundle_run",
      "Submit (without waiting) a run of a job or pipeline defined by one Databricks Asset Bundle, selected by name with `bundle`, using its databricks.yml resource key.",
      "execute",
      {
        type: "object",
        additionalProperties: false,
        properties: { ...selector, key: { type: "string" }, restart: { type: "boolean" } },
        required: ["bundle", "key"],
      },
      resources,
      async (input) =>
        select(input).run({
          key: (input as { key: string }).key,
          restart: (input as { restart?: boolean }).restart === true,
        }),
    ),
    bundleTool(
      "databricks_bundle_destroy",
      "Destroy a Harness-recorded deployment of one Databricks Asset Bundle, selected by name with `bundle`.",
      "write",
      {
        type: "object",
        additionalProperties: false,
        properties: { ...selector, force: { type: "boolean" } },
        required: ["bundle"],
      },
      resources,
      async (input) =>
        select(input).destroy({ force: (input as { force?: boolean }).force === true }),
    ),
  ];
}

/** Map lookup (never record indexing) so inherited keys like `constructor` cannot resolve. */
function selectBundle(
  lifecycles: ReadonlyMap<string, DatabricksAssetBundleLifecycle>,
  names: string[],
  input: JsonObject,
): DatabricksAssetBundleLifecycle {
  const bundle = (input as { bundle?: unknown }).bundle;
  if (typeof bundle !== "string" || !bundle)
    throw new Error(
      `databricks asset bundles: bundle is required; configured bundles: ${names.join(", ")}.`,
    );
  const lifecycle = lifecycles.get(bundle);
  if (!lifecycle)
    throw new Error(
      `databricks asset bundles: unknown bundle "${bundle}"; configured bundles: ${names.join(", ")}.`,
    );
  return lifecycle;
}

function requireNamedBundles(
  lifecycles: ReadonlyMap<string, DatabricksAssetBundleLifecycle>,
): void {
  if (lifecycles.size === 0)
    throw new Error("databricks asset bundles: at least one named bundle is required.");
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function bundleTool(
  name: string,
  description: string,
  effect: "read" | "write" | "execute",
  inputSchema: JsonObject,
  resources: JsonObject[],
  execute: (input: JsonObject) => unknown,
): ToolDef {
  return {
    name,
    description,
    inputSchema,
    metadata: {
      effect,
      provider: "databricks",
      service: "bundles",
      ...(resources.length ? { governance: { resources } } : {}),
    },
    execute,
  };
}

async function collectSourceFiles(dir: string): Promise<string[]> {
  const entries = await readdir(dir, { withFileTypes: true });
  const files: string[] = [];
  for (const entry of entries) {
    const full = path.join(dir, entry.name);
    if (entry.isDirectory()) {
      if (!FINGERPRINT_EXCLUDED_DIRS.has(entry.name))
        files.push(...(await collectSourceFiles(full)));
    } else if (entry.isFile()) {
      files.push(full);
    }
  }
  return files;
}

function requireSuccess(label: string, result: DatabricksCommandResult): void {
  if (result.exitCode === 0) return;
  const detail = result.stderr.trim().slice(-2_000);
  throw new Error(
    `${label} failed with exit code ${result.exitCode}${detail ? `: ${detail}` : ""}`,
  );
}

function lastNonEmptyLine(value: string): string {
  return (
    value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .at(-1) ?? ""
  );
}

function parseBundleRunResult(
  resourceKey: string,
  result: DatabricksCommandResult,
): DatabricksAssetBundleRunResult {
  const records = [...jsonRecords(result.stdout), ...jsonRecords(result.stderr)];
  const jobEvent = records.find((record) => record.type === "job_run_url");
  const pipelineEvent = records.find((record) => record.type === "pipeline_update_url");
  const runUrl =
    stringField(jobEvent, "url") ??
    stringField(pipelineEvent, "url") ??
    firstUrl(result.stdout) ??
    firstUrl(result.stderr);
  const explicitRunId =
    numberField(jobEvent, "run_id") ??
    numberField(jobEvent, "runId") ??
    records.map((record) => numberField(record, "run_id") ?? numberField(record, "runId")).find(
      (value): value is number => value !== undefined,
    );
  const runId = explicitRunId ?? runIdFromUrl(runUrl);
  const pipelineId =
    stringField(pipelineEvent, "pipeline_id") ??
    stringField(pipelineEvent, "pipelineId") ??
    pipelineIdentityFromUrl(runUrl)?.pipelineId;
  const updateId =
    stringField(pipelineEvent, "update_id") ??
    stringField(pipelineEvent, "updateId") ??
    records
      .map((record) => stringField(record, "update_id") ?? stringField(record, "updateId"))
      .find((value): value is string => value !== undefined) ??
    pipelineIdentityFromUrl(runUrl)?.updateId;
  const output = lastNonEmptyLine(result.stdout) || lastNonEmptyLine(result.stderr);
  return {
    submitted: true,
    resourceKey,
    ...(runId !== undefined ? { runKind: "job" as const, runId } : {}),
    ...(pipelineId !== undefined || updateId !== undefined
      ? {
          runKind: "pipeline" as const,
          ...(pipelineId !== undefined ? { pipelineId } : {}),
          ...(updateId !== undefined ? { updateId } : {}),
        }
      : {}),
    ...(runUrl !== undefined ? { runUrl } : {}),
    output,
  };
}

function jsonRecords(value: string): Record<string, unknown>[] {
  const records: Record<string, unknown>[] = [];
  for (const candidate of [value.trim(), ...value.split("\n").map((line) => line.trim())]) {
    if (!(candidate.startsWith("{") && candidate.endsWith("}"))) continue;
    try {
      const parsed = JSON.parse(candidate) as unknown;
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed))
        records.push(parsed as Record<string, unknown>);
    } catch {
      // Progress output may include non-JSON diagnostic lines; text URL parsing handles them.
    }
  }
  return records;
}

function stringField(
  record: Record<string, unknown> | undefined,
  key: string,
): string | undefined {
  const value = record?.[key];
  return typeof value === "string" && value.trim() ? value : undefined;
}

function numberField(
  record: Record<string, unknown> | undefined,
  key: string,
): number | undefined {
  const value = record?.[key];
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && /^\d+$/.test(value)
        ? Number(value)
        : undefined;
  return parsed !== undefined && Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function firstUrl(value: string): string | undefined {
  const match = /https?:\/\/[^\s"']+/u.exec(value);
  return match?.[0]?.replace(/[),.;]+$/u, "");
}

function runIdFromUrl(value: string | undefined): number | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    const raw =
      /\/jobs\/\d+\/runs\/(\d+)/u.exec(url.pathname)?.[1] ??
      /(?:^|\/)job\/\d+\/run\/(\d+)(?:\/|$)/u.exec(url.hash.replace(/^#/, ""))?.[1];
    return raw ? numberField({ value: raw }, "value") : undefined;
  } catch {
    return undefined;
  }
}

function pipelineIdentityFromUrl(
  value: string | undefined,
): { pipelineId: string; updateId: string } | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    const match = /(?:^|\/)pipelines\/([^/]+)\/updates\/([^/?#]+)/u.exec(
      `${url.pathname}/${url.hash.replace(/^#/, "")}`,
    );
    return match?.[1] && match[2]
      ? { pipelineId: match[1], updateId: match[2] }
      : undefined;
  } catch {
    return undefined;
  }
}
